import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const CATEGORIES = ["All", "EB-1A", "EB-2 NIW", "O-1"];

const DEMO = [
  { id: "d1", category: "EB-1A", title: "Machine learning researcher — no RFE", client_field: "Artificial Intelligence", country_of_origin: "India", outcome_year: 2025, summary: "Principal ML researcher at a leading lab, 42 publications, 3,100 citations. Approved without an RFE via premium processing in 12 days.", highlights: ["12-day approval", "42 publications", "3,100 citations"] },
  { id: "d2", category: "EB-2 NIW", title: "Climate policy scientist, first-time filer", client_field: "Atmospheric & Climate Science", country_of_origin: "Brazil", outcome_year: 2024, summary: "PhD conducting IPCC-adjacent research. NIW approved on first filing with a modest publication record but strong national-interest narrative.", highlights: ["First-time filer", "Approved on I-140", "Modest citation count"] },
  { id: "d3", category: "O-1", title: "Ballet choreographer for national company", client_field: "Performing Arts", country_of_origin: "Ukraine", outcome_year: 2025, summary: "Featured guest artist with national press coverage. O-1B approved for a three-year initial term with agent petitioner.", highlights: ["3-year term", "Agent-based petition", "National press evidence"] },
  { id: "d4", category: "EB-1A", title: "Fintech founder, Series B", client_field: "Financial Technology", country_of_origin: "Nigeria", outcome_year: 2024, summary: "Co-founder of a payments platform serving 3M+ users across West Africa. EB-1A approved on the basis of original contributions and critical role.", highlights: ["Critical role", "Original contributions", "High-value startup"] },
  { id: "d5", category: "EB-2 NIW", title: "Semiconductor engineer working on U.S. supply chain", client_field: "Semiconductors", country_of_origin: "Taiwan", outcome_year: 2025, summary: "Senior device engineer at a U.S. chip fabricator. NIW granted with narrative tied to CHIPS Act policy interests.", highlights: ["CHIPS Act nexus", "Advanced degree", "Approved without RFE"] },
  { id: "d6", category: "O-1", title: "Michelin-recognized chef opens U.S. venture", client_field: "Culinary Arts", country_of_origin: "France", outcome_year: 2023, summary: "Executive chef with sustained international press. O-1B approved for a new U.S. restaurant with strong critical acclaim record.", highlights: ["International press", "Critical acclaim", "New U.S. venture"] },
];

export default function SuccessStories() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/success-stories")
      .then((r) => setStories(r.data.length ? r.data : DEMO))
      .catch(() => setStories(DEMO))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter === "All" ? stories : stories.filter((s) => s.category === filter);

  return (
    <div data-testid="success-stories-page">
      <section className="container-x pt-24 pb-16">
        <div className="max-w-3xl">
          <div className="eyebrow fade-up">Case Results</div>
          <h1 className="fade-up d-100 font-display text-5xl lg:text-6xl mt-6 tracking-tight text-[#0F172A] leading-none">
            Selected approvals.
            <br />
            <span className="italic text-[#C2A679]">Every</span> anonymized. Every real.
          </h1>
          <p className="fade-up d-200 mt-8 text-lg text-[#4A4D50] leading-relaxed max-w-2xl">
            A curated set of recent wins across our three practice areas. Names and identifying
            details are withheld to protect client privacy.
          </p>
        </div>
      </section>

      <section className="container-x pb-12">
        <div className="flex flex-wrap gap-3">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/\s+/g, "-")}`}
              onClick={() => setFilter(c)}
              className={`px-5 py-2 text-sm font-medium border transition-colors ${
                filter === c ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-transparent text-[#0F172A] border-[#0F172A]/25 hover:border-[#0F172A]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="container-x pb-24">
        {loading ? (
          <div className="py-24 text-center text-[#4A4D50]">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((s, idx) => (
              <article key={s.id || idx} data-testid={`story-card-${idx}`} className="card-editorial p-8 flex flex-col fade-up">
                <div className="flex items-center justify-between">
                  <span className="font-mono-plex text-xs tracking-[0.2em] uppercase text-[#C2A679]">{s.category}</span>
                  <span className="text-xs text-[#4A4D50]">{s.outcome_year || "—"}</span>
                </div>
                <h3 className="font-display text-xl mt-6 text-[#0F172A] leading-snug">{s.title}</h3>
                <div className="mt-3 text-sm text-[#4A4D50]">
                  {s.client_field}{s.country_of_origin ? ` · ${s.country_of_origin}` : ""}
                </div>
                <p className="mt-6 text-sm text-[#4A4D50] leading-relaxed flex-1">{s.summary}</p>
                {s.highlights?.length > 0 && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {s.highlights.map((h, i) => (
                      <span key={i} className="text-xs px-3 py-1 border border-[#0F172A]/15 text-[#4A4D50]">
                        {h}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-8 pt-6 border-t border-[#0F172A]/10 text-xs eyebrow text-[#1A3626] flex items-center justify-between">
                  <span>Approved</span>
                  <span className="font-mono-plex text-[10px] text-[#4A4D50]">FILE #{String(idx + 1).padStart(3, "0")}</span>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="container-x pb-24">
        <div className="bg-[#0F172A] text-[#F7F5F0] p-12 md:p-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div className="max-w-2xl">
            <div className="eyebrow text-[#C2A679]">Your file next?</div>
            <h3 className="font-display text-3xl md:text-4xl mt-3 leading-tight">Start with a written case review.</h3>
          </div>
          <Link to="/case-review" data-testid="stories-cta" className="btn-primary !bg-[#C2A679] !border-[#C2A679] !text-[#0F172A] hover:!bg-[#D9C7A3]">
            Begin Case Review <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
